/**
 * Normalizes a FS-557 `type='transcript'` DocumentRecord + its markdown body
 * into the view-model the Phase-1 User-Artifact viewer renders.
 *
 * The NestJS `TranscriptArtifactService` writes structured bits to
 * `properties` (summary, participants, actionItems, …). The markdown body
 * remains canonical; the summary falls back to a "## Summary" section of the
 * body when `properties.summary` is absent.
 */
import type { DocumentRecord, TranscriptParticipant } from '@/types/catalog'

export interface UserArtifactModel {
  source: string
  summary: string | null
  participants: TranscriptParticipant[]
  actionItems: string[]
  callTimestamp: string | null
}

/**
 * Pull a named markdown section's body out of the transcript (e.g. "Summary").
 * Matches a `## Heading` line and returns everything up to the next heading of
 * the same-or-higher level. Used as a fallback when `properties.summary` is
 * absent but the body embeds the section.
 */
export function extractMarkdownSection(markdown: string, heading: string): string | null {
  if (!markdown) return null
  const lines = markdown.split('\n')
  // Escape regex metacharacters in the heading so a title like "Q&A (notes)"
  // matches literally instead of being interpreted as a pattern.
  const escapedHeading = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const headingRe = new RegExp(`^(#{1,6})\\s+${escapedHeading}\\s*$`, 'i')
  let start = -1
  let level = 0
  for (let i = 0; i < lines.length; i++) {
    const m = headingRe.exec(lines[i])
    if (m) {
      start = i + 1
      level = m[1].length
      break
    }
  }
  if (start === -1) return null
  const out: string[] = []
  for (let i = start; i < lines.length; i++) {
    const next = /^(#{1,6})\s+/.exec(lines[i])
    if (next && next[1].length <= level) break
    out.push(lines[i])
  }
  const body = out.join('\n').trim()
  return body || null
}

export function buildUserArtifactModel(
  doc: DocumentRecord,
  markdown: string,
): UserArtifactModel {
  const p = doc.properties
  return {
    source: p.source ?? 'meeting_bot',
    // `||` (not `??`) so an empty-string summary also falls back to the body's
    // "## Summary" section instead of rendering "No summary".
    summary: (p.summary && p.summary.trim()) || extractMarkdownSection(markdown, 'Summary'),
    participants: p.participants ?? [],
    actionItems: p.actionItems ?? [],
    callTimestamp: p.callTimestamp ?? null,
  }
}
